import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { resolveCatalogAssetUrl, optimizeImage, imageSrcSet } from "@/lib/image";

/**
 * Builds the list of candidate URLs to try for a brand logo.
 * Primary is always the DB `logo_url` (never constructed from scratch).
 * If it 404s (the stored extension often drifts, e.g. .jpg vs .png), we
 * retry the same path with the other common image extensions before
 * falling back to a letter placeholder.
 */
function buildCandidates(logoUrl: string | null | undefined): string[] {
  const resolved = resolveCatalogAssetUrl(logoUrl);
  if (!resolved) return [];
  const out = [resolved];
  const m = /^(.*)\.([a-z0-9]+)(\?.*)?$/i.exec(resolved);
  if (m) {
    const [, base, , query = ""] = m;
    for (const ext of ["png", "jpg", "jpeg", "webp"]) {
      const candidate = `${base}.${ext}${query}`;
      if (!out.includes(candidate)) out.push(candidate);
    }
  }
  return out;
}

export function BrandLogo({
  logoUrl,
  name,
  className,
  imgClassName,
  width = 96,
  height,
  sizes,
  quality = 60,
}: {
  logoUrl: string | null | undefined;
  name: string;
  /** Wrapper styling (size, border, background). */
  className?: string;
  imgClassName?: string;
  sizes?: string;
  /**
   * Explicit intrinsic height used only to give the <img> a placeholder
   * aspect ratio before it loads (avoids layout shift / CLS warnings).
   * Defaults to half the width, which matches typical wide brand logos.
   */
  height?: number;
  /**
   * Rendered width (CSS px) of the logo. Logos are served by the ops worker,
   * which resizes + serves WebP/AVIF when a `?w=` is requested. Without it the
   * full-size source PNG (often >200 KiB) is downloaded for a ~64px box. We
   * request this width (plus a 2x srcset) so only a few KiB are transferred.
   */
  width?: number;
  /** JPEG/WebP quality για τα λογότυπα (μικρά, ανεκτικά σε συμπίεση). */
  quality?: number;
}) {
  const candidates = useMemo(() => buildCandidates(logoUrl), [logoUrl]);
  const [idx, setIdx] = useState(0);
  const candidate = candidates[idx];
  const src = candidate ? optimizeImage(candidate, { width, quality }) ?? candidate : undefined;
  const srcSet = candidate ? imageSrcSet(candidate, [width, Math.round(width * 1.5)], quality) : undefined;

  if (!src) {
    return (
      <div
        className={cn(
          "grid place-items-center rounded-lg border border-border bg-accent/40 font-bold text-foreground",
          className,
        )}
        aria-label={name}
        title={name}
      >
        {name?.[0]?.toUpperCase() ?? "?"}
      </div>
    );
  }

  return (
    <div className={cn("grid place-items-center overflow-hidden rounded-lg border border-border bg-white", className)}>
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes ?? `${width}px`}
        width={width}
        height={height ?? Math.round(width / 2)}
        alt={name}
        loading="lazy"
        onError={() => setIdx((i) => i + 1)}
        className={cn("block h-auto w-auto max-h-full max-w-full place-self-center object-contain object-center", imgClassName)}
      />
    </div>
  );
}